import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Pill, Textarea } from "../components/UI";
import { Plus, Trash2, Info, CreditCard } from "lucide-react";

const SCOPES = ["global", "account", "campaign", "channel", "spend_commitment"];
const CHANNELS = ["ctv", "video", "display", "native", "audio", "dooh", "email", "direct_mail", "conversational_ai", "data", "attribution"];
const PERIODS = ["monthly", "quarterly", "annual"];

export default function PricingEngine() {
  const [rules, setRules] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    scope: "spend_commitment", channel: "", political_entity_id: "", markup_percent: 40, min_markup_floor: 15,
    min_spend_commitment: 25000, commitment_period: "monthly", active: true, notes: "",
  });

  const [fees, setFees] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [savingFees, setSavingFees] = useState(false);

  const [plans, setPlans] = useState([]);
  const [savingPlan, setSavingPlan] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/pricing/rules"); setRules(data); }
    catch { setRules([]); }
    api.get("/pricing/fees").then(r => setFees(r.data)).catch(() => {});
    api.get("/pricing/ai-usage").then(r => setAiUsage(r.data)).catch(() => {});
    api.get("/subscriptions/plans").then(r => setPlans(r.data)).catch(() => {});
    api.get("/political-entities").then(r => setEntities(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveFees = async () => {
    setSavingFees(true);
    try {
      const { data } = await api.put("/pricing/fees", fees);
      setFees(data);
      api.get("/pricing/ai-usage").then(r => setAiUsage(r.data)).catch(() => {});
    } finally { setSavingFees(false); }
  };
  const setFee = (k, v) => setFees(f => ({ ...f, [k]: v }));

  const setPlanField = (key, field, value) => {
    setPlans(ps => ps.map(p => (p.key === key ? { ...p, [field]: value } : p)));
  };

  const savePlan = async (p) => {
    setSavingPlan(p.key);
    try {
      const features = Array.isArray(p.features) ? p.features : String(p.features || "").split("\n").map(s => s.trim()).filter(Boolean);
      await api.put(`/subscriptions/plans/${p.key}`, {
        price_monthly: parseFloat(p.price_monthly), tagline: p.tagline, features, active: p.active !== false,
      });
    } finally { setSavingPlan(null); }
  };

  const save = async () => {
    const payload = {
      ...form,
      channel: form.channel || null,
      political_entity_id: form.political_entity_id || null,
      markup_percent: parseFloat(form.markup_percent),
      min_markup_floor: parseFloat(form.min_markup_floor),
      min_spend_commitment: parseFloat(form.min_spend_commitment),
    };
    await api.post("/pricing/rules", payload);
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    await api.delete(`/pricing/rules/${id}`); load();
  };

  const entityName = (id) => entities.find(e => e.id === id)?.public_campaign_name || entities.find(e => e.id === id)?.legal_org_name || id;

  const bg = { global: "border-cyan-400/40 bg-cyan-400/5", account: "border-blue-400/40 bg-blue-400/5", channel: "border-purple-400/40 bg-purple-400/5", spend_commitment: "border-emerald-400/40 bg-emerald-400/5", campaign: "border-amber-400/40 bg-amber-400/5" };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Internal Commercial Config"
        title="Pricing Engine"
        subtitle="Media markup (50% global default, account & channel overrides), SaaS subscription plans, platform fees and AI premium."
        action={<Btn onClick={() => setOpen(true)} data-testid="new-rule-btn"><Plus className="w-4 h-4" /> New rule</Btn>}
      />

      <Card>
        <div className="flex items-start gap-3 text-sm text-slate-300">
          <Info className="w-4 h-4 text-cyan-300 mt-0.5" />
          <div>
            <div>Effective markup is chosen by priority: <span className="text-white font-mono">campaign > account+channel > account > channel > spend_commitment > global</span>.</div>
            <div className="text-xs text-slate-500 mt-1">Formula: Client Price = Underlying Cost × (1 + markup%). Default 50% = ~33% gross margin on client revenue. <span className="text-slate-400">Clients only ever see the TotalReach price — supplier costs are never exposed.</span></div>
            <div className="text-xs text-slate-500 mt-1">The campaign builder's "Margin (0–1)" field is the same idea as a decimal — 0.05 = 5%, 0.5 = 50% of the budget kept as margin. <a href="/help?q=margin" className="text-cyan-300 hover:underline" data-testid="pricing-help-link">Full glossary →</a></div>
          </div>
        </div>
      </Card>

      {/* SaaS Subscription Plans */}
      {plans.length > 0 && (
        <Card data-testid="saas-plans-card">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold">SaaS Subscription Plans</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">Monthly platform plans billed via Stripe. Price changes apply to new checkouts immediately (existing subscribers keep their rate until they switch plans).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {plans.map(p => (
              <div key={p.key} className={`rounded-lg border p-4 ${p.highlight ? "border-cyan-400/40 bg-cyan-400/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={`plan-admin-${p.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-bold">{p.name}</div>
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 cursor-pointer">
                    <input type="checkbox" checked={p.active !== false} onChange={(e) => setPlanField(p.key, "active", e.target.checked)} className="accent-cyan-400" />
                    Active
                  </label>
                </div>
                <Input label="Monthly price (USD)" type="number" step="1" value={p.price_monthly}
                  onChange={(e) => setPlanField(p.key, "price_monthly", e.target.value)} data-testid={`plan-price-${p.key}`} />
                <div className="mt-3">
                  <Input label="Tagline" value={p.tagline || ""} onChange={(e) => setPlanField(p.key, "tagline", e.target.value)} />
                </div>
                <div className="mt-3">
                  <Textarea label="Features (one per line)" rows={5}
                    value={Array.isArray(p.features) ? p.features.join("\n") : (p.features || "")}
                    onChange={(e) => setPlanField(p.key, "features", e.target.value)} />
                </div>
                <Btn className="mt-3 w-full" onClick={() => savePlan(p)} disabled={savingPlan === p.key} data-testid={`plan-save-${p.key}`}>
                  {savingPlan === p.key ? "Saving…" : "Save plan"}
                </Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      {fees && (
        <Card data-testid="platform-fees-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="font-display text-lg font-bold">Platform Fees & AI Premium</h3>
              <p className="text-xs text-slate-500 mt-0.5">AI credit premium, audience data markup, and payment processing costs — all admin-configurable.</p>
            </div>
            <Btn onClick={saveFees} disabled={savingFees} data-testid="save-fees-btn">{savingFees ? "Saving…" : "Save fees"}</Btn>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Input label="AI premium %" type="number" step="1" value={fees.ai_premium_pct} onChange={(e) => setFee("ai_premium_pct", e.target.value)} data-testid="fee-ai-premium" />
            <Input label="Data markup %" type="number" step="1" value={fees.data_markup_pct} onChange={(e) => setFee("data_markup_pct", e.target.value)} data-testid="fee-data-markup" />
            <Input label="Platform fee %" type="number" step="0.05" value={fees.platform_fee_pct} onChange={(e) => setFee("platform_fee_pct", e.target.value)} data-testid="fee-platform" />
            <Input label="Merchant fee %" type="number" step="0.05" value={fees.merchant_fee_pct} onChange={(e) => setFee("merchant_fee_pct", e.target.value)} data-testid="fee-merchant" />
            <Input label="Stripe fixed $" type="number" step="0.01" value={fees.stripe_fixed_fee} onChange={(e) => setFee("stripe_fixed_fee", e.target.value)} data-testid="fee-stripe-fixed" />
            <Input label="Card service fee %" type="number" step="0.1" value={fees.cc_fee_pct} onChange={(e) => setFee("cc_fee_pct", e.target.value)} data-testid="fee-cc-pct" />
            <Input label="AI unit cost $" type="number" step="0.01" value={fees.ai_call_unit_cost} onChange={(e) => setFee("ai_call_unit_cost", e.target.value)} data-testid="fee-ai-unit" />
          </div>
          <div className="text-[11px] text-slate-500 mt-3">
            Donation net = amount − platform % − merchant % − Stripe fixed. AI client billing = usage × unit cost × (1 + AI premium). Data client fee = cost CPM × (1 + data markup) — e.g. a $5.00 CPM is billed at ${(5 * (1 + (parseFloat(fees.data_markup_pct) || 0) / 100)).toFixed(2)}.
          </div>
          {aiUsage && aiUsage.rows.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="ai-usage-summary">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">AI credits usage (billed at +{aiUsage.ai_premium_pct}% premium)</div>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
                {aiUsage.rows.map(r => (
                  <div key={r.capability} className="text-slate-400">{r.capability}: <span className="text-white">{r.calls} calls</span> · billed <span className="text-emerald-300">${r.client_billed}</span></div>
                ))}
                <div className="text-slate-300 font-semibold">Total billed: <span className="text-emerald-300">${aiUsage.total_client_billed}</span> <span className="text-slate-500 font-normal">(cost basis ${aiUsage.total_cost_basis})</span></div>
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rules-grid">
        {rules.map(r => (
          <div key={r.id} className={`rounded-lg border p-4 ${bg[r.scope] || "border-white/10"}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">
                  {r.scope.replace(/_/g, " ")}{r.channel ? ` · ${r.channel}` : ""}{r.scope === "account" && r.political_entity_id ? ` · ${entityName(r.political_entity_id)}` : ""}
                </div>
                <div className="font-display text-2xl font-bold brand-gradient mt-1">{r.markup_percent}% markup</div>
                {r.min_spend_commitment > 0 && <div className="text-xs text-slate-300 mt-1">Requires ≥ ${r.min_spend_commitment.toLocaleString()} / {r.commitment_period}</div>}
                {r.min_markup_floor > 0 && <div className="text-xs text-slate-400">Floor: {r.min_markup_floor}%</div>}
                {r.notes && <div className="text-xs text-slate-400 mt-2">{r.notes}</div>}
              </div>
              <div className="text-right shrink-0">
                <Pill status={r.active ? "Active" : "Draft"}>{r.active ? "Active" : "Inactive"}</Pill>
                <button onClick={() => del(r.id)} className="mt-2 text-slate-500 hover:text-rose-300 p-1 block ml-auto"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {rules.length === 0 && <div className="col-span-full text-center py-10 text-slate-500 text-sm">No rules configured (super admin required to create).</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">New pricing rule</h3>
            <div className="grid grid-cols-2 gap-4">
              <Select label="Scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                {SCOPES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </Select>
              <Select label="Channel (optional)" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                <option value="">Any</option>
                {CHANNELS.map(c => <option key={c}>{c}</option>)}
              </Select>
              {form.scope === "account" && (
                <Select label="Account (political entity)" value={form.political_entity_id} onChange={(e) => setForm({ ...form, political_entity_id: e.target.value })} className="col-span-2" data-testid="rule-entity-select">
                  <option value="">Select account…</option>
                  {entities.map(en => <option key={en.id} value={en.id}>{en.public_campaign_name || en.legal_org_name}</option>)}
                </Select>
              )}
              <Input label="Markup %" type="number" step="0.5" value={form.markup_percent} onChange={(e) => setForm({ ...form, markup_percent: e.target.value })} />
              <Input label="Minimum floor %" type="number" step="0.5" value={form.min_markup_floor} onChange={(e) => setForm({ ...form, min_markup_floor: e.target.value })} />
              {form.scope === "spend_commitment" && (
                <>
                  <Input label="Min spend commitment (USD)" type="number" value={form.min_spend_commitment} onChange={(e) => setForm({ ...form, min_spend_commitment: e.target.value })} />
                  <Select label="Commitment period" value={form.commitment_period} onChange={(e) => setForm({ ...form, commitment_period: e.target.value })}>
                    {PERIODS.map(p => <option key={p}>{p}</option>)}
                  </Select>
                </>
              )}
              <div className="col-span-2">
                <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="save-rule-btn">Save rule</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
